import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { FiSun as Sun, FiMoon as Moon, FiX as X, FiCheckCircle as CheckCircle } from 'react-icons/fi';
import { assessmentAPI, apiCall } from '../services/realApi';
import { API_BASE_URL, IS_STATIC_DEMO } from '../services/config';
import { DEMO_USER_LIST } from '../services/demoUsers';
import { setToken } from '../utils/auth';

const STEP_EMAIL = 'email';
const STEP_SSO = 'sso';
const STEP_PASSWORD = 'password';

type LoginStep = typeof STEP_EMAIL | typeof STEP_SSO | typeof STEP_PASSWORD;

interface SsoInfo {
  has_sso: boolean;
  enforcement_mode?: string;
  auth_provider?: string;
}

interface JoinData {
  email: string;
  name: string;
}

interface DemoUser {
  email: string;
  role: string;
  description: string;
}

const GoogleIcon: React.FC = () => (
  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState<LoginStep>(IS_STATIC_DEMO ? STEP_PASSWORD : STEP_EMAIL);
  const [ssoCheckLoading, setSsoCheckLoading] = useState(false);
  const [ssoInfo, setSsoInfo] = useState<SsoInfo | null>(null);

  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinData, setJoinData] = useState<JoinData>({ email: '', name: '' });
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [assessmentIdFromUrl, setAssessmentIdFromUrl] = useState<string | null>(null);

  const [ssoRedirectLoading, setSsoRedirectLoading] = useState(false);

  const { login } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const successMessage = (location.state as { message?: string } | null)?.message;

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const ssoErrorParam = urlParams.get('sso_error');
    if (ssoErrorParam) {
      const errorMessages: Record<string, string> = {
        validation_failed: 'SSO authentication failed. Please try again.',
        user_not_found: 'No account found. Contact your administrator.',
        account_inactive: 'Your account has been deactivated.',
      };
      setError(errorMessages[ssoErrorParam] ?? 'SSO login failed.');
    }
  }, [location.search]);

  useEffect(() => {
    if (IS_STATIC_DEMO) return;

    const urlParams = new URLSearchParams(location.search);
    const assessmentId = urlParams.get('assessment_id');

    if (assessmentId) {
      setAssessmentIdFromUrl(assessmentId);
      setShowJoinModal(true);
    }
  }, [location.search]);

  const handleEmailContinue = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSsoCheckLoading(true);

    try {
      const response = await apiCall('/auth/sso/check', {
        method: 'POST',
        body: JSON.stringify({ email }),
        requireAuth: false,
      }) as SsoInfo;

      setSsoInfo(response);

      if (response.has_sso && response.enforcement_mode === 'required') {
        await redirectToSSO();
        return;
      }

      if (!response.has_sso && response.auth_provider === 'google') {
        redirectToGoogle();
        return;
      }

      if (response.has_sso) {
        setStep(STEP_SSO);
      } else {
        setStep(STEP_PASSWORD);
      }
    } catch {
      setStep(STEP_PASSWORD);
    } finally {
      setSsoCheckLoading(false);
    }
  };

  const redirectToSSO = async () => {
    setSsoRedirectLoading(true);
    try {
      const response = await apiCall('/auth/sso/init', {
        method: 'POST',
        body: JSON.stringify({ email }),
        requireAuth: false,
      }) as { redirect_url: string };
      globalThis.location.href = response.redirect_url;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to initiate SSO login.';
      setError(message);
      setSsoRedirectLoading(false);
      setStep(STEP_PASSWORD);
    }
  };

  const redirectToGoogle = () => {
    const baseUrl = API_BASE_URL.replace('/api/v1', '');
    globalThis.location.href = `${baseUrl}/api/v1/auth/oauth/google/init?email=${encodeURIComponent(email)}`;
  };

  const handleBackToEmail = () => {
    setStep(STEP_EMAIL);
    setPassword('');
    setError('');
    setSsoInfo(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password) as { success: boolean; error?: string };

      if (result.success) {
        const urlParams = new URLSearchParams(location.search);
        const assessmentId = urlParams.get('assessment_id');

        if (assessmentId) {
          navigate(`/hub/assessments?assessment_id=${assessmentId}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else {
        setError(result.error ?? 'Login failed. Please check your credentials.');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setJoinError(null);
    setJoinLoading(true);

    try {
      const response = await assessmentAPI.joinAssessment(assessmentIdFromUrl ?? '', joinData) as {
        pendingApproval?: boolean;
        token?: string;
      };

      if (response.pendingApproval) {
        setPendingApproval(true);
        setJoinSuccess(true);
      } else if (response.token) {
        setToken(response.token);
        setJoinSuccess(true);

        setTimeout(() => {
          navigate(`/hub/assessments?assessment_id=${assessmentIdFromUrl ?? ''}`, { replace: true });
          globalThis.location.reload();
        }, 1500);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to join assessment';
      setJoinError(message);
    } finally {
      setJoinLoading(false);
    }
  };

  const renderEmailField = (readOnly: boolean) => {
    const isLive = !IS_STATIC_DEMO;
    const labelClass = isLive
      ? 'block text-sm font-medium text-gray-300'
      : 'block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors';
    const inputClass = isLive
      ? 'appearance-none block w-full px-3 py-2 border border-gray-700 rounded-lg placeholder-gray-500 bg-gray-800 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors'
      : 'appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors';
    const readOnlyClass = isLive
      ? 'flex items-center justify-between px-3 py-2 border border-gray-700 rounded-lg bg-gray-800/50 text-white sm:text-sm transition-colors'
      : 'flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white sm:text-sm transition-colors';

    return (
      <div>
        <label htmlFor="email" className={labelClass}>
          Email address
        </label>
        <div className="mt-1">
          {readOnly ? (
            <div className={readOnlyClass}>
              <span>{email}</span>
              <button
                type="button"
                onClick={handleBackToEmail}
                className="text-blue-400 hover:text-blue-300 text-sm font-medium ml-2"
              >
                Change
              </button>
            </div>
          ) : (
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              className={inputClass}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors">
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors"
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
        </button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src={isDarkMode ? '/lens-logo-dark.png' : '/lens-logo-light.png'}
            alt="Aivilo Lens"
            className="h-16 w-auto"
          />
        </div>
        {IS_STATIC_DEMO && (
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400 transition-colors">
            Multi-tenant SaaS platform for AWS Partners
          </p>
        )}
      </div>

      {IS_STATIC_DEMO && (
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10 transition-colors">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {renderEmailField(false)}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md text-sm transition-colors">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>

            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600 transition-colors" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors">Demo Users</span>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {(DEMO_USER_LIST as DemoUser[]).map((demoUser) => (
                  <button
                    key={demoUser.email}
                    onClick={() => {
                      setEmail(demoUser.email);
                      setPassword('password');
                    }}
                    className="w-full text-left p-3 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{demoUser.role}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{demoUser.email}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{demoUser.description}</div>
                  </button>
                ))}
              </div>

              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center transition-colors">
                Click any demo user above to auto-fill credentials (password: &quot;password&quot;)
              </p>
            </div>
          </div>
        </div>
      )}

      {!IS_STATIC_DEMO && (
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm px-6">
          {successMessage && (
            <div className="bg-green-900/40 border border-green-700 text-green-400 px-4 py-3 rounded-lg text-sm mb-6 transition-colors">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-400 px-4 py-3 rounded-lg text-sm mb-6 transition-colors">
              {error}
            </div>
          )}

          {step === STEP_EMAIL && (
            <form className="space-y-5" onSubmit={handleEmailContinue}>
              {renderEmailField(false)}
              <button
                type="submit"
                disabled={ssoCheckLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              >
                {ssoCheckLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Checking...
                  </span>
                ) : 'Continue'}
              </button>
            </form>
          )}

          {step === STEP_SSO && (
            <div className="space-y-5">
              {renderEmailField(true)}

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => void redirectToSSO()}
                  disabled={ssoRedirectLoading}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                >
                  {ssoRedirectLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Redirecting to SSO...
                    </span>
                  ) : 'Continue with SSO'}
                </button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-900 text-gray-500">or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={redirectToGoogle}
                  className="w-full flex items-center justify-center py-2.5 px-4 border border-gray-700 rounded-lg text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-gray-500 transition-colors"
                >
                  <GoogleIcon />
                  Continue with Google
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep(STEP_PASSWORD)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Use password instead
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === STEP_PASSWORD && (
            <form className="space-y-5" onSubmit={handleSubmit}>
              {renderEmailField(true)}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 transition-colors">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    autoFocus
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-700 rounded-lg placeholder-gray-500 bg-gray-800 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Forgot your password?
                </button>
              </div>

              <div>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-900 text-gray-500">or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={redirectToGoogle}
                  className="mt-4 w-full flex items-center justify-center py-2.5 px-4 border border-gray-700 rounded-lg text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-gray-500 transition-colors"
                >
                  <GoogleIcon />
                  Continue with Google
                </button>
              </div>

              {ssoInfo?.has_sso && (
                <div className="text-center pt-3 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={() => setStep(STEP_SSO)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Use SSO instead
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      )}

      {!IS_STATIC_DEMO && showJoinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors">
                Join Assessment
              </h2>
              <button
                onClick={() => {
                  setShowJoinModal(false);
                  setJoinData({ email: '', name: '' });
                  setJoinError(null);
                  setJoinSuccess(false);
                  setPendingApproval(false);
                  navigate('/login', { replace: true });
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {joinSuccess ? (
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  {pendingApproval ? (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                        Request Submitted
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 transition-colors">
                        Your request to join this assessment has been submitted. You&apos;ll receive an email once an existing team member approves your access.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                        Welcome!
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 transition-colors">
                        Redirecting you to the assessment...
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <form onSubmit={handleJoin} className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors mb-4">
                    Join this assessment to collaborate with your team
                  </p>

                  {joinError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-600 dark:text-red-400">{joinError}</p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="join-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name
                    </label>
                    <input
                      id="join-name"
                      type="text"
                      value={joinData.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJoinData({ ...joinData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="join-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      id="join-email"
                      type="email"
                      value={joinData.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJoinData({ ...joinData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      placeholder="john@example.com"
                      required
                    />
                  </div>

                  <div className="flex space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowJoinModal(false);
                        setJoinData({ email: '', name: '' });
                        setJoinError(null);
                        navigate('/login', { replace: true });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      disabled={joinLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={joinLoading}
                    >
                      {joinLoading ? 'Joining...' : 'Join Assessment'}
                    </button>
                  </div>

                  <div className="text-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setShowJoinModal(false);
                          setJoinData({ email: '', name: '' });
                          setJoinError(null);
                        }}
                        className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                      >
                        Sign in here
                      </button>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
